import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useParams } from 'react-router-dom';
import { SeoContentAnalysis, ContentAnalysisStatus } from '@/types/api/seo';
import * as Styles from './SeoContentList.styles';

type Props = {
  analyses: SeoContentAnalysis[];
  isLoading: boolean;
  onNewAnalysis: () => void;
};

const statusColor: Record<ContentAnalysisStatus, 'default' | 'info' | 'success' | 'error'> = {
  [ContentAnalysisStatus.QUEUED]: 'default',
  [ContentAnalysisStatus.PROCESSING]: 'info',
  [ContentAnalysisStatus.COMPLETE]: 'success',
  [ContentAnalysisStatus.FAILED]: 'error',
};

const SeoContentListComponent = function ({ analyses, isLoading, onNewAnalysis }: Props): JSX.Element {
  const { projectId = '' } = useParams();
  const basePath = `/seox/projects/${projectId}`;
  return (
    <Box sx={{}}>
      <Styles.PageHeader>
        <Styles.Title>Content Optimizer</Styles.Title>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewAnalysis}
          sx={{ backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' } }}
        >
          New Analysis
        </Button>
      </Styles.PageHeader>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#054AD9' }} />
        </Box>
      ) : analyses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No content analyses yet. Analyze a page to get optimization recommendations.
          </Typography>
        </Box>
      ) : (
        <Styles.TableWrapper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Target Keyword</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyses.map((analysis) => (
                  <TableRow
                    key={analysis.id}
                    component={Link}
                    to={`${basePath}/content/${analysis.shortId}`}
                    sx={{
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {analysis.url}
                      </Typography>
                    </TableCell>
                    <TableCell>{analysis.targetKeyword}</TableCell>
                    <TableCell align="center">
                      {analysis.overallScore !== null ? (
                        <Typography
                          fontWeight={600}
                          color={
                            analysis.overallScore >= 80
                              ? 'success.main'
                              : analysis.overallScore >= 50
                                ? 'warning.main'
                                : 'error.main'
                          }
                        >
                          {analysis.overallScore}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(analysis.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={analysis.status}
                        color={statusColor[analysis.status]}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Styles.TableWrapper>
      )}
    </Box>
  );
};

export default SeoContentListComponent;
